/**
 * @file NoticeList.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-11-03
 * @description The notices, rendered — shared by the dashboard's Notices
 *              widget and the status bar's alerts window (Cam, 15 Oct 2026),
 *              so there is one list, not two. Moved out of NoticesWidget
 *              unchanged, plus acknowledging.
 *
 * Everything comes from GET /api/notices (useNotices): which notices this
 * person sees, what each found, and which rows they have acknowledged.
 *
 *   - A notice with something to say is a section: title, count, summary,
 *     its rows (each opening its record — EntityLink). Collapsible.
 *   - Each row: Acknowledge (just for you — it stops counting towards your
 *     Alerts; everyone else still sees it). An acknowledged row is muted:
 *     "Acknowledged by you · Undo". It goes for everybody when the issue is
 *     fixed and the check next runs.
 *   - "Acknowledge all" per notice sends every row shown.
 *   - Before the migration acknowledging is greyed with the reason
 *     (never hidden).
 *   - A notice that found nothing is listed underneath as "all clear".
 *
 * 1.1.0 (2026-11-03, Cam — "a persistant alert"): a notice the server sends
 * with `acknowledge: false` has no ✓, no "Acknowledge all" and no Undo — it
 * says its `persistentText` instead and always counts. A row's `actions`
 * ([{ id, label, icon, url, title }]) are buttons on it (e.g. "Send to Xero
 * now"), handed to `onAction(notice, item, action)`.
 *
 * 1.2.0 (2026-11-03, merged with the Invoice Detail variation review's flags):
 * a row's actions are labelled buttons under the row (NoticeItemActions) —
 * each greyed with the server's `block` reason, asking its `confirm` first
 * (ConfirmDialog, `confirmText`), then `onAction(notice, item, action)`. An
 * action names its endpoint as `url` (or `path`) and may carry a `body`.
 */

import { useState } from 'react';
import { formatDate } from '../utils/dateFormatters';
import { formatCurrency } from '../utils/currencyFormatter';
import EntityLink from './EntityLink';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import '../widgets/NoticesWidget.css';

const SEVERITY_ICON = { error: 'error', warning: 'warning', info: 'info' };

function NoticeTag({ tag }) {
  const cls = `notice-tag notice-tag-${tag.tone || 'muted'}`;
  if (tag.entity?.type && tag.entity?.id) {
    return (
      <EntityLink type={tag.entity.type} id={tag.entity.id} className={cls} variant="badge">
        {tag.text}
      </EntityLink>
    );
  }
  return <span className={cls}>{tag.text}</span>;
}

/** May this notice's rows be acknowledged? (false: a persistent alert) Pure. */
export const acknowledgeable = (notice) => !!notice && notice.acknowledge !== false;

/** A row's own actions (server-defined), each a labelled button greyed with its reason. */
function NoticeItemActions({ notice, item, busy, onAction }) {
  const [asking, setAsking] = useState(null);
  const actions = Array.isArray(item.actions) ? item.actions : [];
  if (!actions.length) return null;
  const go = (a) => {
    setAsking(null);
    if (onAction && a) onAction(notice, item, a);
  };
  return (
    <div className="notice-item-actions">
      {actions.map(a => {
        const why = a.block || (!onAction ? 'Open the record to do this' : (busy ? 'Working…' : null));
        return (
          <button
            key={a.id || a.key || a.url || a.path || a.label}
            type="button"
            data-write-action
            className={`notice-item-action${why ? ' btn-disabled' : ''}`}
            onClick={() => (a.confirm ? setAsking(a) : go(a))}
            disabled={!!why}
            title={why || a.confirm || a.title || a.label}
            aria-label={`${a.label} — ${item.ref}`}
          >
            {a.icon && <span className="material-symbols-rounded">{a.icon}</span>}
            {a.label}
          </button>
        );
      })}
      <ConfirmDialog
        isOpen={!!asking}
        type="warning"
        title={asking?.label || ''}
        message={asking?.confirm || ''}
        confirmText={asking?.confirmText || asking?.label || 'Confirm'}
        confirmIcon={asking?.icon || 'check'}
        cancelText="Not now"
        onConfirm={() => go(asking)}
        onCancel={() => setAsking(null)}
      />
    </div>
  );
}

function NoticeItem({ notice, item, ackReady, ackReason, busy, onAcknowledge, onUndo, onAction, actionBusy }) {
  const pair = [{ notice: notice.key, key: item.key }];
  const disabledWhy = !ackReady ? ackReason : (busy ? 'Saving…' : null);
  const ackable = acknowledgeable(notice);
  const acked = ackable && item.acknowledged;
  return (
    <li className={`notice-item${acked ? ' notice-item-acknowledged' : ''}`} data-item={item.key}>
      <div className="notice-item-line">
        {item.entity?.type
          ? <EntityLink type={item.entity.type} id={item.entity.id} className="notice-item-ref">{item.ref}</EntityLink>
          : <span className="notice-item-ref">{item.ref}</span>}
        <span className="notice-item-title" title={item.title}>{item.title}</span>
        {item.amount != null && <span className="notice-item-amount">{formatCurrency(item.amount)}</span>}
        {onAcknowledge && ackable && !item.acknowledged && (
          <button
            type="button"
            className={`btn-icon notice-ack${disabledWhy ? ' btn-disabled' : ''}`}
            onClick={() => onAcknowledge(pair)}
            disabled={!!disabledWhy}
            title={disabledWhy || 'Acknowledge — it stops counting in your Alerts (just for you) until it is fixed or comes back'}
            aria-label={`Acknowledge ${item.ref}`}
          >
            <span className="material-symbols-rounded">done</span>
          </button>
        )}
      </div>
      <div className="notice-item-meta">
        {item.date && <span className="notice-item-date">{item.dateLabel ? `${item.dateLabel} ` : ''}{formatDate(item.date)}</span>}
        {(item.tags || []).map((tag, i) => <NoticeTag key={i} tag={tag} />)}
      </div>
      {item.detail && <div className="notice-item-detail">{item.detail}</div>}
      <NoticeItemActions notice={notice} item={item} busy={actionBusy} onAction={onAction} />
      {acked && (
        <div className="notice-item-acked">
          Acknowledged by you ·{' '}
          <button
            type="button"
            className="notice-undo"
            onClick={() => onUndo && onUndo(pair)}
            disabled={!!disabledWhy || !onUndo}
            title={disabledWhy || 'Count it in your Alerts again'}
          >
            Undo
          </button>
        </div>
      )}
    </li>
  );
}

function AcknowledgeAll({ notice, pending, ackReady, ackReason, busy, onAcknowledge }) {
  useSlideOutButton([pending.length, ackReady, busy]);
  const why = !ackReady ? ackReason : (busy ? 'Saving…' : null);
  return (
    <div className="buttons slideout notice-ack-all">
      <div>
        <button
          type="button"
          className={`btn-slideout${why ? ' btn-disabled' : ''}`}
          onClick={() => onAcknowledge(pending.map(i => ({ notice: notice.key, key: i.key })))}
          disabled={!!why}
          title={why || `Acknowledge all ${pending.length} shown — just for you`}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
          <span className="btn-label">Acknowledge all</span>
        </button>
      </div>
    </div>
  );
}

function NoticeSection({ notice, collapsed, onToggle, ackReady, ackReason, busy, onAcknowledge, onUndo, onAction, actionBusy }) {
  const sev = notice.error ? 'error' : (notice.severity || 'info');
  const ackable = acknowledgeable(notice);
  const unack = !ackable || notice.unacknowledged == null ? notice.count : notice.unacknowledged;
  const pending = (notice.items || []).filter(i => !ackable || !i.acknowledged);
  const allAcked = !notice.error && notice.count > 0 && unack === 0;
  return (
    <section className={`notice notice-${sev}${allAcked ? ' notice-acknowledged' : ''}`} data-notice={notice.key}>
      <button
        type="button"
        className="notice-head"
        onClick={onToggle}
        aria-expanded={!collapsed}
        title={notice.help || notice.title}
      >
        <span className="material-symbols-rounded notice-icon">{notice.icon || SEVERITY_ICON[sev]}</span>
        <span className="notice-title">{notice.title}</span>
        {!notice.error && (
          <span
            className="notice-count"
            title={unack === notice.count ? `${notice.count}` : `${unack} not acknowledged by you, of ${notice.count}`}
          >
            {unack === notice.count ? notice.count : `${unack}/${notice.count}`}
          </span>
        )}
        <span className="material-symbols-rounded notice-chevron">{collapsed ? 'expand_more' : 'expand_less'}</span>
      </button>
      {notice.error ? (
        <div className="notice-summary">{notice.error}</div>
      ) : (
        <>
          {notice.summary && <div className="notice-summary">{notice.summary}</div>}
          {!collapsed && (
            <>
              {notice.help && <div className="notice-help">{notice.help}</div>}
              {!ackable && (
                <div className="notice-persistent">
                  <span className="material-symbols-rounded">push_pin</span>
                  {notice.persistentText || 'Stays until it is put right — it cannot be acknowledged.'}
                </div>
              )}
              {onAcknowledge && ackable && pending.length > 1 && (
                <AcknowledgeAll notice={notice} pending={pending} ackReady={ackReady} ackReason={ackReason}
                  busy={busy} onAcknowledge={onAcknowledge} />
              )}
              <ul className="notice-items">
                {(notice.items || []).map(item => (
                  <NoticeItem key={item.key} notice={notice} item={item} ackReady={ackReady} ackReason={ackReason}
                    busy={busy} onAcknowledge={onAcknowledge} onUndo={onUndo} onAction={onAction} actionBusy={actionBusy} />
                ))}
              </ul>
              {notice.more > 0 && (
                <div className="notice-more">and {notice.more} more not shown</div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** How many of these notices' rows this person has not acknowledged (error / warning only). Pure. */
export function unacknowledgedTotal(notices) {
  return (notices || [])
    .filter(n => !n.error && (n.severity === 'error' || n.severity === 'warning'))
    .reduce((sum, n) => sum + (!acknowledgeable(n) || n.unacknowledged == null ? (n.count || 0) : n.unacknowledged), 0);
}

/**
 * @param {object} p
 * @param {object|null} p.data       GET /api/notices
 * @param {boolean} p.loading
 * @param {boolean} p.failed
 * @param {Function} p.onRetry
 * @param {object} p.collapsed       { [noticeKey]: true }
 * @param {Function} p.onToggle      (noticeKey) => void
 * @param {Function} [p.onAcknowledge] (items) => void
 * @param {Function} [p.onUndo]        (items) => void
 * @param {boolean} [p.busy]
 * @param {string|null} [p.error]
 * @param {Function} [p.onAction]    (notice, item, action) => void — a row's own action
 * @param {boolean} [p.actionBusy]
 * @param {string|null} [p.info]     the last action's answer
 */
export default function NoticeList({
  data, loading, failed, onRetry, collapsed = {}, onToggle,
  onAcknowledge, onUndo, busy = false, error = null, onAction, actionBusy = false, info = null
}) {
  const notices = data?.notices || [];
  const ackReady = data?.ack ? data.ack.ready !== false : true;
  const ackReason = data?.ack?.reason || null;

  if (loading && !data) return <div className="notices-empty">Checking…</div>;
  if (failed && !data) {
    return (
      <div className="notices-empty notices-failed">
        <span className="material-symbols-rounded">cloud_off</span>
        Notices could not be loaded.
        <button type="button" className="btn-icon" onClick={onRetry} title="Try again" aria-label="Try again">
          <span className="material-symbols-rounded">refresh</span>
        </button>
      </div>
    );
  }
  if (notices.length === 0) return <div className="notices-empty">There are no notices for your role.</div>;

  const open = notices.filter(n => n.error || n.count > 0);
  const clear = notices.filter(n => !n.error && !(n.count > 0));
  return (
    <>
      {error && (
        <div className="notices-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          {error}
        </div>
      )}
      {info && (
        <div className="notices-info" role="status">
          <span className="material-symbols-rounded">info</span>
          {info}
        </div>
      )}
      {open.length === 0 && (
        <div className="notices-empty notices-all-clear">
          <span className="material-symbols-rounded">check_circle</span>
          All clear
        </div>
      )}
      {open.map(n => (
        <NoticeSection
          key={n.key}
          notice={n}
          collapsed={!!collapsed?.[n.key]}
          onToggle={() => onToggle && onToggle(n.key)}
          ackReady={ackReady}
          ackReason={ackReason}
          busy={busy}
          onAcknowledge={onAcknowledge}
          onUndo={onUndo}
          onAction={onAction}
          actionBusy={actionBusy}
        />
      ))}
      {clear.length > 0 && (
        <ul className="notices-clear">
          {clear.map(n => (
            <li key={n.key} title={n.emptyText || ''}>
              <span className="material-symbols-rounded">check</span>
              {n.title}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
